// src/utils/speedmatchEnforcer.js
import axios from "axios";

/* ----------------------- module-scope guards ----------------------- */
const _enforcerCooldown = new Map(); // eventId -> timestamp
let _enforcerInFlight = new Set();   // keys: `${eventId}:${pageKind}`
const cdMs = 200;                    // tiny debounce to prevent churn

function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
    "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

/* -------------------------------------------------------------
   Tiny helpers for robust client guards (decline → no flash)
--------------------------------------------------------------*/
function nowMs() { return Date.now(); }

// Per-match “I just declined this match” flag (with timestamp TTL).
function isLocallyDeclined(matchId, ttlMs = 15000) {
  if (!matchId) return false;
  try {
    const key = `sm:declineExit:${matchId}`;
    const atKey = `sm:declineExit:${matchId}:at`;
    const flag = sessionStorage.getItem(key) === "1";
    if (!flag) return false;
    const at = Number(sessionStorage.getItem(atKey) || 0);
    // If older code didn’t stamp :at, still treat as recent just once.
    if (!at) return true;
    return nowMs() - at < ttlMs;
  } catch {
    return false;
  }
}

// One-shot global debounce right after *any* decline.
function touchedGlobalDecline(ttlMs = 1000) {
  try {
    const at = Number(sessionStorage.getItem("sm:lastDeclinedAt") || 0);
    return at && (nowMs() - at < ttlMs);
  } catch {
    return false;
  }
}

// Read a "until" timestamp from sessionStorage (or 0)
function readUntil(key) {
  try { return Number(sessionStorage.getItem(key) || 0); }
  catch { return 0; }
}

// Promote-safe recheck to avoid racing server flips.
async function rereadStateOnce({ api, eventId }) {
  const url = `${api}/matches/speedmatch/${eventId}/state?t=${Date.now()}`;
  const res = await axios.get(url, {
    ...authHeaders(),
    validateStatus: (s) => [200, 202, 204, 401, 403, 404, 410].includes(s)
  });
  return res?.data || {};
}

/**
 * Server-enforced navigation for Speedmatch.
 */
export async function enforceSpeedmatchState({ api, eventId, pageKind, navigate }) {
  const started = nowMs();
  console.log("[Enforcer] START", { api, eventId, pageKind, now: started });

  // Debounce rapid repeats per event
  const cdUntil = _enforcerCooldown.get(eventId) || 0;
  if (started < cdUntil) return true;
  _enforcerCooldown.set(eventId, started + cdMs);
  setTimeout(() => {
    if (_enforcerCooldown.get(eventId) <= started) _enforcerCooldown.delete(eventId);
  }, cdMs + 50);

  // Re-entrancy guard for concurrent calls (route churn)
  const key = `${eventId}:${pageKind}`;
  if (_enforcerInFlight.has(key)) return true;
  _enforcerInFlight.add(key);
  const done = () => _enforcerInFlight.delete(key);

  try {
    // 1) Fetch server truth
    let data;
    try {
      const url = `${api}/matches/speedmatch/${eventId}/state?t=${Date.now()}`;
      console.log("[Enforcer] FETCH", url);
      const res = await axios.get(url, {
        ...authHeaders(),
        validateStatus: (s) => [200, 202, 204, 401, 403, 404, 410].includes(s)
      });
      data = res.data || {};
      console.log("[Enforcer] FETCH_OK", data);
    } catch (err) {
      console.log("[Enforcer] FETCH_ERR", err?.message || err);
      return true; // fail open (don’t rip the user away)
    }

    let want = String(data?.route || "portal"); // 'portal' | 'partner' | 'navigate' | 'verify' | 'inmatch'
    let endMs = Number.isFinite(data?.endMs) ? Number(data.endMs) : null;
    let matchId = data?.matchId || null;
    const nextAllowedPromotionAt = Number(data?.nextAllowedPromotionAt || 0);
    console.log("[Enforcer] SERVER_STATE", { want, endMs, matchId, nextAllowedPromotionAt });

    // Promotion targets (used by multiple guards below)
    const promotionTargets = new Set(["partner", "navigate", "verify", "inmatch"]);

    // Respect server-side promotion window hint
    if (promotionTargets.has(want) && nextAllowedPromotionAt > nowMs()) {
      const delay = Math.max(0, nextAllowedPromotionAt - nowMs() + 50);
      console.log("[Enforcer] PROMOTION_DELAY server hint → retry in", delay, "ms");
      setTimeout(() => {
        enforceSpeedmatchState({ api, eventId, pageKind, navigate });
      }, delay);
      return true;
    }

    // ─────────────────────────────────────────────────────────────
    // Intro block (partner → navigate) during overlay
    // ─────────────────────────────────────────────────────────────
    try {
      const gateUntil = readUntil(`speedmatch_intro_gate:${eventId}`);
      const holdUntil = readUntil(`speedmatch_intro_hold:${eventId}`);
      const blockUntil = Math.max(gateUntil, holdUntil);
      if (pageKind === "partner" && want === "navigate" && blockUntil && nowMs() < blockUntil) {
        const delay = Math.max(0, blockUntil - nowMs() + 20);
        console.log("[Enforcer] INTRO_BLOCK active → delaying promotion for", delay, "ms");
        setTimeout(() => {
          enforceSpeedmatchState({ api, eventId, pageKind, navigate });
        }, delay);
        return true;
      }
    } catch (e) {
      console.log("[Enforcer] intro block read error", e);
    }

    // ─────────────────────────────────────────────────────────────
    // Verify-success HOLD (verify → inmatch)
    // ─────────────────────────────────────────────────────────────
    try {
      const successHoldUntil = readUntil(`speedmatch_verify_success_hold:${eventId}`);
      if (pageKind === "verify" && want === "inmatch" && successHoldUntil && nowMs() < successHoldUntil) {
        const delay = Math.max(0, successHoldUntil - nowMs() + 20);
        console.log("[Enforcer] VERIFY_SUCCESS_HOLD → delaying promotion to inmatch for", delay, "ms");
        setTimeout(() => {
          enforceSpeedmatchState({ api, eventId, pageKind, navigate });
        }, delay);
        return true;
      }
    } catch (e) {
      console.log("[Enforcer] verify-success hold read error", e);
    }

    // 2) Local decline guard (per match, 15s TTL)
    try {
      if (want === "partner" && matchId && isLocallyDeclined(matchId)) {
        console.log("[Enforcer] LOCAL_DECLINE_GUARD → forcing 'portal' for just-declined match", { matchId });
        want = "portal";
        matchId = null;
        endMs = null;
      }
    } catch (e) {
      console.log("[Enforcer] decline guard read error", e);
    }

    // 3) Global debounce after any decline
    if (promotionTargets.has(want) && touchedGlobalDecline(1000)) {
      console.log("[Enforcer] GLOBAL_DECLINE_DEBOUNCE → rechecking before promotion…");
      try {
        const again = await rereadStateOnce({ api, eventId });
        const newWant = String(again?.route || "portal");
        const newEnd = Number.isFinite(again?.endMs) ? Number(again.endMs) : null;
        const newMatch = again?.matchId || null;
        console.log("[Enforcer] GLOBAL_DECLINE_DEBOUNCE_RECHECK", { newWant, newEnd, newMatch });

        if (newWant !== want || newMatch !== matchId) {
          want = newWant;
          endMs = newEnd;
          matchId = newMatch;
        }
      } catch (e) {
        console.log("[Enforcer] GLOBAL_DECLINE_DEBOUNCE_RETRY_ERR", e?.message || e);
      }
    }

    const now = nowMs();

    // HARD back intent (verify → navigate)
    try {
      const forceUntil = Number(sessionStorage.getItem(`speedmatch_force_navigate:${eventId}`) || 0);
      if (pageKind === 'verify' && now < forceUntil) {
        console.log('[Enforcer] FORCE_NAVIGATE due to back intent');
        want = 'navigate';
      }
    } catch {}

    // Suppress verify during back-intent
    try {
      const forceUntil2 = Number(sessionStorage.getItem(`speedmatch_force_navigate:${eventId}`) || 0);
      if (pageKind === 'navigate' && want === 'verify' && now < forceUntil2) {
        console.log('[Enforcer] NAV_BACK_SUPPRESS verify during back intent');
        return true;
      }
    } catch {}
      
    // 4) Sticky InMatch
    try {
      const localInmatchUntil = Number(sessionStorage.getItem(`speedmatch_inmatch_until:${eventId}`) || 0);
      if (pageKind === "inmatch" && localInmatchUntil > now + 1500 && want !== "inmatch") {
        console.log("[Enforcer] STICKY_INMATCH keep local", { localInmatchUntil });
        return true;
      }
    } catch (e) {
      console.log("[Enforcer] sticky inmatch read error", e);
    }

    // 5) Post-session grace
    try {
      const until = Number(sessionStorage.getItem(`speedmatch_block_return:${eventId}`) || 0);
      if (want === "partner" && Number.isFinite(until) && now < until) {
        console.log("[Enforcer] BLOCK_RETURN active until", until);
        return true;
      }
    } catch (e) {
      console.log("[Enforcer] block return read error", e);
    }

    // 6) Back-block from verify
    try {
      const backBlock = Number(sessionStorage.getItem(`speedmatch_back_block:${eventId}`) || 0);
      if (want === "verify" && now < backBlock) {
        console.log("[Enforcer] BACK_BLOCK suppress verify until", backBlock);
        return true;
      }
    } catch (e) {
      console.log("[Enforcer] back block read error", e);
    }

    // 7) If we’re already where server wants us, noop
    if (want === pageKind) {
      console.log("[Enforcer] NOOP already on", pageKind);
      if (pageKind === 'navigate') {
        try {
          sessionStorage.removeItem(`speedmatch_force_navigate:${eventId}`);
          sessionStorage.removeItem(`speedmatch_back_block:${eventId}`);
        } catch {}
      }
      return true;
    }

    // 8) Demotion debounce from inmatch
    if (pageKind === "inmatch") {
      const ok = data?.route === "inmatch" && endMs && endMs > now + 500;
      if (!ok) {
        console.log("[Enforcer] DEMOTION_DEBOUNCE checking again…");
        try {
          await new Promise((r) => setTimeout(r, 350));
          const again = await rereadStateOnce({ api, eventId });
          const againEnd = Number.isFinite(again?.endMs) ? Number(again.endMs) : null;
          const stillNotInMatch = !(again?.route === "inmatch" && againEnd && againEnd > nowMs() + 500);
          if (!stillNotInMatch) {
            console.log("[Enforcer] DEMOTION_CANCELLED still in match after retry");
            return true;
          }
          console.log("[Enforcer] DEMOTION_CONFIRMED moving out of InMatch", again);
        } catch (err) {
          console.log("[Enforcer] DEMOTION_RETRY_ERR", err?.message || err);
          return true;
        }
      }
    }

    // 9) Safety: no promotion without matchId
    if (promotionTargets.has(want) && !matchId) {
      console.log("[Enforcer] SAFETY: promotion without matchId → falling back to portal");
      want = "portal";
    }

    // 10) Navigate (and seed inmatch stickiness).
    try {
      if (want === "inmatch" && Number.isFinite(endMs)) {
        sessionStorage.setItem(`speedmatch_inmatch_until:${eventId}`, String(endMs));
      }
      console.log("[Enforcer] NAVIGATE", { from: pageKind, to: want, matchId, endMs });
    } catch (e) {
      console.log("[Enforcer] set lock error", e);
    }

    navigate(pathFor({ want }), {
      replace: true,
      state: { matchId, stageEndsAt: endMs || null, fromEnforcer: true },
    });

    try {
      sessionStorage.removeItem(`speedmatch_force_navigate:${eventId}`);
      sessionStorage.removeItem(`speedmatch_back_block:${eventId}`);
    } catch {}

    return false;
  } finally {
    done();
  }
}

function pathFor({ want }) {
  switch (want) {
    case "navigate":
      return "/navigatespotpage";
    case "partner":
      return "/match-found";
    case "verify":
      return "/code-verification";
    case "inmatch":
      return "/inmatchpage";
    case "portal":
    default:
      return "/matchflow";
  }
}

/**
 * Bind a listener that re-runs the enforcer whenever we receive a route pulse.
 * Returns a cleanup function; call it in your component's useEffect cleanup.
 */
export function bindRoutePulseEnforcer({ api, eventId, pageKind, navigate }) {
  const handler = (e) => {
    try {
      const detail = e?.detail || {};
      if (detail?.eventId && String(detail.eventId) !== String(eventId)) return;
      enforceSpeedmatchState({ api, eventId, pageKind, navigate });
    } catch {}
  };
  window.addEventListener('sm:route-pulse', handler);
  return () => window.removeEventListener('sm:route-pulse', handler);
}
